import { IsDefined, ValidateNested} from 'class-validator';
import { computed } from 'mobx';
import * as uuid from 'uuid';
import * as moment from 'moment';

import { EntityModel } from 'src/core/services/data/models';
import { DataService, property } from 'src/core/services/data.service';
import { BookemZulu as Model } from 'src/core/services/data/JayDataContext';
import { ObjectUtils } from 'src/core/utils/object';
import { StatusTexts } from "src/app/shared/status-texts";

export class QuoteDataService extends DataService<typeof Model.Bookings.Logic.ViewModels.QuoteItem, Model.Bookings.Logic.ViewModels.QuoteItem, QuoteItem> {
	protected map(entity: Model.Bookings.Logic.ViewModels.QuoteItem): QuoteItem {
		return new QuoteItemModel(entity);
	}

	static make(ctx?: any): QuoteDataService {
		return new QuoteDataService(ctx, ctx => ctx.Quotes);
	}

	add(userId: number, defaultDestiny: number, defaultLanguage: string, defaultMarket: number, defaultBrand: number) {
		const entity = new Model.Bookings.Logic.ViewModels.QuoteItem();
		entity.status = 0;
		entity.quoteNumber = 'Quote';
		entity.documentsLibraryId = uuid.v4();
		(<any>entity).dynamicProperties = {
			notes: []
		};
		entity.paymentMode = 0;
		entity.reservationDate = new Date();
		entity.dateFrom = new Date();
		entity.dateTo = new Date();
		entity.totalCost = '0';
		entity.totalSale = '0';
		entity.totalSaleGross = '0';
		entity.paid = '0';
		entity.managedById = userId;
		entity.languageCode = defaultLanguage;
		entity.travelRegionId = defaultDestiny;
		entity.marketId = defaultMarket;
		entity.brandId = defaultBrand;

		return entity;
	}

	updateQuote(quote: QuoteItem, depositPercent: number, dueDateInDays: number, taxes: TaxSummary[], items: QuoteEntryItem[], isRestoration: boolean): void {
		let dateFrom: moment.Moment = isRestoration ? moment.utc(quote.dateFrom) : undefined;
		let dateTo: moment.Moment = isRestoration ? moment.utc(quote.dateTo) : undefined;
		let totalCost = isRestoration ? parseFloat(quote.totalCost.toString()) : 0;
		let totalSale = isRestoration ? parseFloat(quote.totalSale.toString()) : 0;
		let totalSaleGross = isRestoration ? parseFloat(quote.totalSaleGross.toString()) : 0;
		let totalSalesmanProfit = isRestoration ? parseFloat(quote.totalSalesmanProfit.toString()) : 0;
		for (const entry of items) {
			if (entry.parentEntryId != null && (entry.productType === 6 || entry.productType === 7)) {
				continue;
			}
			const costPrice = entry.costPrice * entry.dynamicProperties.sellPriceResult.exchangeRate;
			const sellPrice = parseFloat(entry.sellPrice.toString());
			totalCost += costPrice;
			totalSale += sellPrice;
			const tax = taxes.find(e => e.id == entry.dynamicProperties.taxId);
			const calculatedSalePriceGross = sellPrice * (1 + parseFloat(tax ? tax.percent.toString() : '0'));
			if (!entry.dynamicProperties.hasSaleAdjustment) {
				entry.salePriceGross = calculatedSalePriceGross;
				entry.salesmanProfit = calculatedSalePriceGross - costPrice;
			} else if (entry.dynamicProperties.adjustmentType === 'relative') {
				const adjustment = items.find(m => m.parentEntryId === entry.id && m.productType === 7);
				entry.salePriceGross = calculatedSalePriceGross + adjustment.salePriceGross;
			}
			entry.dynamicProperties.calculatedSalePriceGross = calculatedSalePriceGross;

			totalSaleGross += entry.salePriceGross;
			totalSalesmanProfit += entry.salesmanProfit;
			const entryDateFrom = moment.utc(entry.dateFrom);
			if (
				!entry.dynamicProperties.isFee
				&& entry.productType !== 6
				&& entry.productType !== 7
				&& entry.productType !== 8
				&& (dateFrom == null || entryDateFrom.isBefore(dateFrom))) {
				dateFrom = entryDateFrom;
			}
			const entryDateTo = moment.utc(entry.dateTo);
			if (dateTo == null || entryDateTo.isAfter(dateTo)) {
				dateTo = entryDateTo;
			}
		}
		totalCost = ObjectUtils.round(totalCost);
		totalSale = ObjectUtils.round(totalSale);
		totalSaleGross = ObjectUtils.round(totalSaleGross);
		totalSalesmanProfit = ObjectUtils.round(totalSalesmanProfit);
		const deposit = ObjectUtils.round(totalSaleGross * depositPercent);
		if (dateFrom && !dateFrom.isSame(quote.dateFrom, 'minute')) {
			quote.dateFrom = dateFrom.toDate();
		}
		if (dateTo && !dateTo.isSame(quote.dateTo, 'minute')) {
			quote.dateTo = dateTo.toDate();
		}
		if (quote.totalCost != totalCost) {
			quote.totalCost = totalCost;
		}
		if (quote.totalSale != totalSale) {
			quote.totalSale = totalSale;
		}
		if (quote.totalSaleGross != totalSaleGross) {
			quote.totalSaleGross = totalSaleGross;
		}
		if (quote.totalSalesmanProfit != totalSalesmanProfit) {
			quote.totalSalesmanProfit = totalSalesmanProfit;
		}
		if (quote.deposit !== deposit) {
			quote.deposit = deposit;
		}
		quote.invoiceDueDate = moment.utc(quote.dateFrom).add(dueDateInDays, 'day').endOf('day').toDate();
	}
}

export class QuoteItemModel extends EntityModel<Model.Bookings.Logic.ViewModels.QuoteItem, QuoteItem> implements QuoteItem {
	@property<QuoteItemModel>(o => o.entity.id, (o, value) => o.entity.id = value)
	public id: number;

	@property<QuoteItemModel>(o => o.entity.clientId, (o, value) => o.entity.clientId = value)
	@IsDefined({ message: 'custumer is required' })
	public clientId: number;

	@property<QuoteItemModel>(o => o.entity.marketId, (o, value) => o.entity.marketId = value)
	@IsDefined({ message: 'market is required' })
	public marketId: number;

	@property<QuoteItemModel>(o => o.entity.brandId, (o, value) => o.entity.brandId = value)
	@IsDefined({ message: 'brand is required' })
	public brandId: number;

	@property<QuoteItemModel>(o => o.entity.status, (o, value) => o.entity.status = value)
	public status: number;

	@property<QuoteItemModel>(o => o.entity.languageCode, (o, value) => o.entity.languageCode = value)
	@IsDefined({ message: 'language is required' })
	public languageCode: string;

	@property<QuoteItemModel>(o => o.entity.travelRegionId, (o, value) => o.entity.travelRegionId = value)
	public travelRegionId: number;

	@property<QuoteItemModel>(o => o.entity.reservationDate, (o, value) => o.entity.reservationDate = value)
	@IsDefined({ message: 'reservation date is required' })
	public reservationDate: Date;

	@property<QuoteItemModel>(o => o.entity.bookingDate, (o, value) => o.entity.bookingDate = value)
	public bookingDate: Date;

	@property<QuoteItemModel>(o => o.entity.confirmedDate, (o, value) => o.entity.confirmedDate = value)
	public confirmedDate: Date;

	@property<QuoteItemModel>(o => o.entity.dateFrom, (o, value) => o.entity.dateFrom = value)
	public dateFrom: Date;

	@property<QuoteItemModel>(o => o.entity.dateTo, (o, value) => o.entity.dateTo = value)
	public dateTo: Date;

	@property<QuoteItemModel>(o => parseFloat(o.entity.totalCost.toString()), (o, value) => o.entity.totalCost = value)
	public totalCost: number;

	@property<QuoteItemModel>(o => parseFloat(o.entity.totalSale.toString()), (o, value) => o.entity.totalSale = value)
	public totalSale: number;

	@property<QuoteItemModel>(o => parseFloat(o.entity.totalSaleGross.toString()), (o, value) => o.entity.totalSaleGross = value)
	public totalSaleGross: number;

	@property<QuoteItemModel>(o => parseFloat(o.entity.totalSalesmanProfit.toString()), (o, value) => o.entity.totalSalesmanProfit = value)
	public totalSalesmanProfit: number;

	@property<QuoteItemModel>(o => parseFloat(o.entity.paid.toString()), (o, value) => o.entity.paid = value)
	public paid: number;

	@property<QuoteItemModel>(o => parseFloat(o.entity.deposit.toString()), (o, value) => o.entity.deposit = value)
	public deposit: number;

	@property<QuoteItemModel>(o => o.entity.overrideDeposit, (o, value) => o.entity.overrideDeposit = value)
	public overrideDeposit: boolean;

	@property<QuoteItemModel>(o => o.entity.invoiceDueDate, (o, value) => o.entity.invoiceDueDate = value)
	public invoiceDueDate: Date;

	@property<QuoteItemModel>(o => o.entity.overrideInvoiceDueDate, (o, value) => o.entity.overrideInvoiceDueDate = value)
	public overrideInvoiceDueDate: boolean;

	@property<QuoteItemModel>(o => o.entity.quoteNumber, (o, value) => o.entity.quoteNumber = value)
	public quoteNumber: string;

	@property<QuoteItemModel>(o => o.entity.parentQuoteId, (o, value) => o.entity.parentQuoteId = value)
	public parentQuoteId?: number;

	@property<QuoteItemModel>(o => o.entity.parentQuote, (o, value) => o.entity.parentQuote = value)
	public parentQuote: any;

	@property<QuoteItemModel>(o => o.entity.clientDisplayName, (o, value) => o.entity.clientDisplayName = value)
	public clientDisplayName?: string;

	@property<QuoteItemModel>(o => o.entity.agentId, (o, value) => o.entity.agentId = value)
	public agentId?: number;

	@property<QuoteItemModel>(o => o.entity.agentName, (o, value) => o.entity.agentName = value)
	public agentName?: string;

	@property<QuoteItemModel>(o => o.entity.paymentMode, (o, value) => o.entity.paymentMode = value)
	public paymentMode: number;

	@property<QuoteItemModel>(o => o.entity.defaultTaxId, (o, value) => o.entity.defaultTaxId = value)
	public defaultTaxId: number;

	@property<QuoteItemModel>(o => o.entity.managedById, (o, value) => o.entity.managedById = value)
	@IsDefined({ message: 'managedBy is required' })
	public managedById: number;

	@property<any>(o => o.entity.dynamicProperties, (o, value) => o.entity.dynamicProperties = value)
	//  @Validate(QuoteDPValidation)
	public dynamicProperties: {};

	@property<QuoteItemModel>(o => o.entity.children, (o, value) => o.entity.children = value)
	public children: any[];

	@property<QuoteItemModel>(o => o.entity.paxs, (o, value) => o.entity.paxs = value)
	public paxs: any[];

	@property<QuoteItemModel>(o => o.entity.invoices, (o, value) => o.entity.invoices = value)
	public invoices: any[];

	@property<QuoteItemModel>(o => o.entity.entries, (o, value) => o.entity.entries = value)
	@ValidateNested()
	public entries: any[];

	@property<QuoteItemModel>(o => o.entity.documentsLibraryId, (o, value) => o.entity.documentsLibraryId = value)
	public documentsLibraryId: string;

	@computed
	public get statusText(): string {
		return StatusTexts.getQuoteStatusFromNumber(this.entity.status);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quotes/form/quote-form.data.ts